import { AdditionalContext } from '../api_types';
import { IDocContext } from '../document_transformer_service';
import { log } from '../log';
import { DuoProjectAccessChecker } from '../services/duo_access';
import {
  AdvancedContextResolver,
  ContextResolution,
} from './context_resolvers/advanced_context_resolver';
import { OpenTabsResolver } from './context_resolvers/open_tabs_context_resolver';
import { filterContextResolutions } from './advanced_context_filters';
import { CODE_SUGGESTIONS_REQUEST_BYTE_SIZE_LIMIT } from './helpers';

type AdvancedContextResolverFactory = () => AdvancedContextResolver;

const advancedContextResolverFactories: AdvancedContextResolverFactory[] = [
  OpenTabsResolver.getInstance,
];

export const getAdvancedContext = async ({
  documentContext,
  dependencies: { duoProjectAccessChecker },
}: {
  documentContext: IDocContext;
  dependencies: { duoProjectAccessChecker: DuoProjectAccessChecker };
}): Promise<ContextResolution[]> => {
  try {
    const resolvers = advancedContextResolverFactories.map((factory) => factory());
    const resolutions: ContextResolution[] = [];
    for (const resolver of resolvers) {
      // eslint-disable-next-line no-await-in-loop
      for await (const resolution of resolver.buildContext({ documentContext })) {
        resolutions.push(resolution);
      }
    }
    const filteredResolutions = await filterContextResolutions({
      contextResolutions: resolutions,
      documentContext,
      dependencies: { duoProjectAccessChecker },
      byteSizeLimit: CODE_SUGGESTIONS_REQUEST_BYTE_SIZE_LIMIT,
    });
    log.debug(`Advanced Context Factory: using ${filteredResolutions.length} context resolutions `);
    return filteredResolutions;
  } catch (e) {
    log.error('Advanced Context: Error while getting advanced context', e);
    return [];
  }
};

/**
 * Maps `ContextResolution` to the request body format `AdditionalContext`.
 * Note: We intentionally keep these two separate to avoid coupling the request body
 * format to the advanced context resolver internal data.
 */
export function advancedContextToRequestBody(
  advancedContext: ContextResolution[],
): AdditionalContext[] {
  return advancedContext.map((ac) => ({
    type: ac.type,
    name: ac.fileRelativePath,
    content: ac.content,
    resolution_strategy: ac.strategy,
  }));
}
